import React, {useEffect, useState} from 'react';
import BudgetPage from '../../components/new-ui/project/project-budget-v3/BudgetPage';
import {createFragmentContainer, graphql} from 'react-relay';
import Util from '../../forecast-app/shared/util/util';
import RatesAndInternalCostPage from '../../components/new-ui/project/project-budget-v3/rates_and_internal_cost_page';
import ExpensesPage from '../../components/new-ui/project/project-budget-v3/expenses_page';
import HelpCenterIcon from '../../components/new-ui/help_center_icon';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import {useIntl} from 'react-intl';
import {BudgetContentWrapper, BudgetHeaderWrapper, BudgetWrapper, Tab, Tabs} from './project_budget_v3.styled';
import {BUDGET_TYPE, PROJECT_STATUS} from '../../constants';
import BudgetWarningTab from '../../components/new-ui/project/project-budget-v3/components/budget_warning_tab';
import FinancialCalculationTrigger, {
	financialCalculationTriggerQuery,
} from '../../components/new-ui/project/project-budget-v3/loaders/FinancialCalculationTrigger';
import {LoadMore} from '../../components/loaders/LoadMore';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import RevenueRecognitionPage from '../../components/new-ui/project/project-budget-v3/RevenueRecognitionPage';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import ProjectTaskReportPage, {projectTaskReportPageQuery} from './budget/task_report/project_task_report_page';
import ForecastQueryRenderer from '../../ForecastQueryRenderer';
import {EVENT_ID, subscribe, unsubscribe} from '../event_manager';
import {createProjectUpdateToast} from '../../components/new-ui/project/project-budget-v3/util/BudgetUtils';
import BudgetWarningsLoader, {budgetWarningsLoaderQuery} from './BudgetWarningsLoader';
import * as tracking from '../../tracking';

const ProjectBudgetV3 = props => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {availableFeatureFlags} = props.viewer;
	const {modules} = props.viewer.company;
	const [retryCallback, setRetryCallback] = useState();
	const [retryProcessing, setRetryProcessing] = useState(false);
	const [retryProps, setRetryProps] = useState();
	tracking.trackPage('Project-budget');

	const hasFinancials = Util.hasFinance(modules);
	const hasBaseline = Util.hasBaselineModule(modules, props.viewer.userType) && props.viewer.project.useBaseline;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();
	const hasRevenueRecognition = hasFeatureFlag('revenue_recognition');

	let tabs = {DETAILS: 'project_budget.details'};
	let retryWarningQuery;

	const showRevenueRecognition = hasRevenueRecognition && props.viewer.project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	if (showRevenueRecognition) {
		tabs = {
			...tabs,
			REVENUE_RECOGNITION: 'project_budget.revenue_recognition',
			TASK_REPORT: 'project_budget.task_breakdown',
		};
	}

	if (hasFinancials && hasPermission(PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION)) {
		tabs = {
			...tabs,
			RATES_AND_INTERNAL_COST: 'project_budget.rates_and_internal_cost',
			EXPENSES: 'project_budget.expenses',
		};
	}

	const [selectedTab, setSelectedTab] = useState(() => {
		const savedSelectedTab = localStorage.getItem('project-budget-v3-last-selected-tab');
		const savedTabExists = Object.values(tabs).includes(savedSelectedTab);
		return hasFinancials && savedTabExists ? savedSelectedTab : tabs.DETAILS;
	});

	const updateProject = props => {
		if (retryCallback) {
			retryCallback();
			setRetryProcessing(true);
			setRetryProps(props);
		}
		if (retryWarningQuery) {
			retryWarningQuery();
		}
	};

	const handleRetryCompletion = result => {
		if (!result.timestamp && !retryProps?.noToast) {
			createProjectUpdateToast(intl);
		}
	};

	function handleFinancialCalculationTriggerResponse(retry, result) {
		if (!retryCallback) {
			setRetryCallback(() => retry);
		}
		if (retryProcessing) {
			handleRetryCompletion(result);
			setRetryProcessing(false);
		}
	}

	useEffect(() => {
		subscribe(EVENT_ID.BUDGET_UPDATE_PROJECT, updateProject);
		return () => {
			unsubscribe(EVENT_ID.BUDGET_UPDATE_PROJECT, updateProject);
		};
	});

	const changeTab = tab => {
		updateProject({noToast: true});
		setSelectedTab(tab);
	};

	const renderSelectedTab = result => {
		Util.localStorageSetItem('project-budget-v3-last-selected-tab', selectedTab);
		if (retryProcessing || !retryCallback) {
			tracking.trackPageLoad(selectedTab);
		}
		switch (selectedTab) {
			case tabs.DETAILS:
				return (
					<BudgetPage
						key={'BudgetPage'}
						timestamp={result.timestamp}
						viewer={props.viewer}
						project={props.viewer.project}
						company={props.viewer.company}
						retry={props.retry}
						hasFinancials={hasFinancials}
						hasBaseline={hasBaseline}
						hasLimitedFinancialAccess={hasLimitedFinancialAccess}
					/>
				);

			case tabs.RATES_AND_INTERNAL_COST:
				return (
					<RatesAndInternalCostPage
						key={'RatesAndInternalCostPage'}
						timestamp={result.timestamp}
						project={props.viewer.project}
						company={props.viewer.company}
						relay={props.relay}
						retry={props.retry}
						availableFeatureFlags={availableFeatureFlags}
					/>
				);

			case tabs.EXPENSES:
				return (
					<ExpensesPage
						key={'ExpensesPage'}
						timestamp={result.timestamp}
						project={props.viewer.project}
						company={props.viewer.company}
						retry={props.retry}
						availableFeatureFlags={availableFeatureFlags}
					/>
				);

			case tabs.REVENUE_RECOGNITION:
				return (
					<RevenueRecognitionPage
						key={'RecognitionPage'}
						timestamp={result.timestamp}
						viewer={props.viewer}
						retry={props.retry}
						availableFeatureFlags={availableFeatureFlags}
						project={props.viewer.project}
						company={props.viewer.company}
					/>
				);

			case tabs.TASK_REPORT:
				return (
					<ForecastQueryRenderer
						key="query-render-project-task-report"
						componentName="project-task-report"
						query={projectTaskReportPageQuery}
						showLoader={true}
						variables={{
							projectId: props.viewer.project.id,
						}}
						render={relayProps => <ProjectTaskReportPage {...relayProps} />}
					/>
				);
			default:
				return null;
		}
	};

	const getHeaderTitle = () => {
		const content = [];

		content.push(<HelpCenterIcon key={'help-center-icon'} href={'https://help.forecast.it/project-budget'} />);

		if (props.viewer.project.status !== PROJECT_STATUS.DONE && props.viewer.project.status !== PROJECT_STATUS.HALTED) {
			content.push(
				<div key={'top_header_warning_button'} className="warning-button">
					<LoadMore
						key="query-render-project-budget-warnings"
						query={budgetWarningsLoaderQuery}
						variables={{
							projectId: props.viewer.project.id,
						}}
						componentName="project_budget_warnings_loader"
						loader={<BudgetWarningsLoader />}
					>
						{({data, retry}) => {
							retryWarningQuery = retry;
							return (
								<BudgetWarningTab
									setSelectedTab={tab => setSelectedTab(tab)}
									hasFinancials={hasFinancials}
									project={data.project}
									tabs={tabs}
									viewer={data.viewer}
									relay={props.relay}
									retry={retry}
								/>
							);
						}}
					</LoadMore>
				</div>
			);
		}

		if (showRevenueRecognition) {
			const email = props.viewer.email;
			content.push({
				type: TopHeaderBar.TYPE.FEEDBACK,
				link:
					'https://www.forecast.app/feedback-revenue-recognition' +
					(email ? '?email=' + encodeURIComponent(email) : ''),
			});
		}

		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar key={'budget_v3_top_header_bar'} title={formatMessage({id: 'common.budget'})} content={content} />
			</TopHeaderBarWrapper>
		);
	};

	const allTabs = Object.values(tabs);

	return (
		<LoadMore
			key="financial-calculation-trigger"
			query={financialCalculationTriggerQuery}
			variables={{
				projectId: props.viewer.project.id,
			}}
			componentName="financial_calculation_trigger"
			loader={<FinancialCalculationTrigger />}
		>
			{(result, retry) => {
				handleFinancialCalculationTriggerResponse(retry, result);
				return (
					<BudgetWrapper data-cy={'budget-page'}>
						{props.children} {/* renders the task modal if open */}
						<BudgetHeaderWrapper>{getHeaderTitle()}</BudgetHeaderWrapper>
						<BudgetContentWrapper>
							<Tabs>
								{allTabs.map(tab => (
									<Tab
										key={tab}
										className={selectedTab === tab ? ' active' : ''}
										onClick={() => changeTab(tab)}
										data-cy={tab.replace('.', '_')}
										data-userpilot={`${tab}_tab`}
									>
										{formatMessage({id: tab})}
									</Tab>
								))}
							</Tabs>
							{renderSelectedTab(result)}
						</BudgetContentWrapper>
					</BudgetWrapper>
				);
			}}
		</LoadMore>
	);
};

const projectBudgetV3Query = graphql`
	query projectBudgetV3_Query($projectId: String) {
		viewer {
			actualPersonId
			userType
			project(id: $projectId) {
				id
			}
			component(name: "project_budget_v3", projectId: $projectId)
			...projectBudgetV3_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {projectBudgetV3Query};

export default createFragmentContainer(ProjectBudgetV3, {
	viewer: graphql`
		fragment projectBudgetV3_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
			id
			email
			project(id: $projectId) {
				id
				budgetType
				useBaseline
				projectEndYear
				projectEndMonth
				projectEndDay
				companyProjectId
				...BudgetPage_project
				...expensesPage_project
				...ratesAndInternalCostPage_project
				...RevenueRecognitionPage_project
			}
			company {
				id
				...BudgetPage_company
				...expensesPage_company
				...ratesAndInternalCostPage_company
				...RevenueRecognitionPage_company
				modules {
					moduleType
				}
			}
		}
	`,
});
